// Detect which connected sources a Torah source GENUINELY cites in its own words -
// the real mar'eh makom. This is the earned, honest basis for a "Cited in this
// text" label (as opposed to "commentary ON this", which is the opposite
// direction).
//
// DUAL GATE (accuracy-lock): a ref is treated as cited ONLY IF
//   (1) it is a real ref Sefaria returned in getLinks for this source (the LINK
//       gate - already true of every candidate we are handed), AND
//   (2) its Hebrew title + primary location number actually appear, adjacent, in
//       the source's own Hebrew text (the TEXT gate).
// We CONFIRM known real refs against the text; we NEVER resolve a free-text token
// into a guessed ref. Conservative by design: a miss (false negative) is fine, a
// false claim is not.
//
// Calibrated against real Sefaria text. Rashi on Numbers 15:38 cites yechezkel 8,
// menachot 42, shir hashirim 2 in Hebrew, and getLinks returns Ezekiel 8:3,
// Menachot 42a/42b, Song of Songs 2:9 - so the intersection is exactly the works
// Rashi quotes, while Sifrei Bamidbar (not named in the text) and the base verse
// are correctly left out.
//
// IMPLEMENTATION NOTE: the Hebrew-mark + dash regex classes are built with RegExp
// + \\u-escaped STRING literals (pure ASCII) on purpose. This repo's formatter runs
// in a cp1252 environment and silently rewrites literal combining marks + en/em
// dashes (it turned a literal dash class into an invalid range once), so we keep
// every fragile range out of the source as ASCII escapes.

const GEMATRIA: Readonly<Record<string, number>> = {
  "א": 1, "ב": 2, "ג": 3, "ד": 4, "ה": 5, "ו": 6,
  "ז": 7, "ח": 8, "ט": 9, "י": 10,
  "כ": 20, "ך": 20, "ל": 30, "מ": 40, "ם": 40,
  "נ": 50, "ן": 50, "ס": 60, "ע": 70,
  "פ": 80, "ף": 80, "צ": 90, "ץ": 90,
  "ק": 100, "ר": 200, "ש": 300, "ת": 400,
};

const RE_DASH = new RegExp("[\\u05BE\\u2013\\u2014-]", "g"); // maqaf + en/em dash + hyphen
const RE_MARKS = new RegExp("[\\u0591-\\u05C7\\u05F3\\u05F4]", "g"); // nikud, te'amim, geresh/gershayim
const RE_QUOTE = /['"]/g; // ASCII apostrophe/quote used as geresh/gershayim
const NOT_HEB = new RegExp("[^\\u05D0-\\u05EA]+"); // a run of non Hebrew-letters (tokenizer)

// Strip HTML tags, nikud, te'amim/cantillation, geresh/gershayim, maqaf and dashes,
// then collapse whitespace - so titles and numbers match regardless of pointing or
// markup.
export function stripHebrew(s: string): string {
  return s
    .replace(/<[^>]*>/g, " ")
    .replace(RE_DASH, " ")
    .replace(RE_MARKS, "")
    .replace(RE_QUOTE, "")
    .replace(/\s+/g, " ")
    .trim();
}

// Gematria value of a Hebrew-letter run (final letters = base value; non-letters
// ignored). 0 when the token holds no Hebrew letters.
export function gematria(token: string): number {
  let sum = 0;
  let any = false;
  for (const ch of token) {
    const v = GEMATRIA[ch];
    if (v !== undefined) {
      sum += v;
      any = true;
    }
  }
  return any ? sum : 0;
}

// The primary location number of a ref: the daf for Talmud ("Menachot 42a:3" ->
// 42), the chapter for Tanakh ("Ezekiel 8:3" -> 8). null when none is present.
export function primaryLocation(ref: string): number | null {
  const daf = ref.match(/\s(\d+)[ab](?::|\s|$)/); // Talmud daf + amud
  if (daf) return parseInt(daf[1], 10);
  const chap = ref.match(/\s(\d+):\d+/); // chapter:verse
  if (chap) return parseInt(chap[1], 10);
  const bare = ref.match(/\s(\d+)\s*$/); // trailing bare number
  return bare ? parseInt(bare[1], 10) : null;
}

export interface CitationCandidate {
  ref: string;
  titleHe: string | null; // the cited work's Hebrew title (collectiveTitle.he)
}

// Does `text` (already stripped) mention `title` immediately followed - within a
// short window of up to 3 Hebrew tokens - by a Hebrew numeral whose gematria
// equals `loc`? (The window lets a "daf"/"perek" word sit between the title and
// the number.) Short-token + exact-gematria + adjacency make a coincidental match
// very unlikely.
function textCites(text: string, title: string, loc: number): boolean {
  let from = 0;
  for (;;) {
    const i = text.indexOf(title, from);
    if (i < 0) return false;
    from = i + title.length;
    const after = text.slice(from, from + 14);
    const tokens = after.split(NOT_HEB).filter(Boolean).slice(0, 3);
    for (const tok of tokens) {
      if (tok.length <= 3 && gematria(tok) === loc) return true;
    }
  }
}

// The subset of `candidates` (refs Sefaria linked to the source) that the source's
// own Hebrew text genuinely cites. `baseRef` - the ref the source is built ON - is
// excluded, since the base text is not a citation.
export function citedInText(
  sourceHe: string,
  candidates: readonly CitationCandidate[],
  baseRef?: string | null,
): Set<string> {
  const text = stripHebrew(sourceHe);
  const out = new Set<string>();
  if (!text) return out;
  for (const c of candidates) {
    if (baseRef && c.ref === baseRef) continue; // the base text is not a citation
    if (!c.titleHe) continue;
    const title = stripHebrew(c.titleHe);
    if (title.length < 2) continue; // too short to anchor on safely
    const loc = primaryLocation(c.ref);
    if (loc === null) continue;
    if (textCites(text, title, loc)) out.add(c.ref);
  }
  return out;
}
